import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
// Self-hosted fonts — bundled so the app renders fully offline (native app /
// underground parking = no signal). Replaces the Google Fonts CDN <link>s.
import '@fontsource/manrope/700.css';
import '@fontsource/manrope/800.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import 'material-symbols/outlined.css';
import './styles/design-tokens.css';
import './styles/components.css';
import App from './App';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  initialScope: {
    tags: {
      app_type: 'pwa',
    },
  },
});

// PWA chunk-load recovery.
// After a deploy, the user's cached index.html may reference JS chunk hashes
// that no longer exist on the server → dynamic import() throws ChunkLoadError →
// React renders nothing → blank white screen on navigation. This listener
// detects that exact failure and hard-reloads once to pick up the new build.
// Guarded with sessionStorage so we never loop if the reload itself fails.
const CHUNK_RELOAD_KEY = 'lotngo-chunk-reload-attempted';
function isChunkLoadError(reason: unknown): boolean {
  if (!reason) return false;
  const message = reason instanceof Error ? reason.message : String(reason);
  return /Loading chunk \d+ failed|ChunkLoadError|Importing a module script failed|Failed to fetch dynamically imported module/i.test(message);
}
function handleChunkLoadError(reason: unknown): void {
  if (!isChunkLoadError(reason)) return;
  try {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1') return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
  } catch {
    // sessionStorage may be unavailable (private mode, quota) — reload anyway.
  }
  Sentry.captureMessage('Chunk load error — forcing reload to recover from stale PWA cache', 'warning');
  window.location.reload();
}
window.addEventListener('error', (event) => handleChunkLoadError(event.error || event.message));
window.addEventListener('unhandledrejection', (event) => handleChunkLoadError(event.reason));

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

// On native (Capacitor) we migrate any TWA-era localStorage into Preferences
// and pre-warm the in-memory cache used by Zustand's persist storage BEFORE
// rendering the app. On the browser these helpers are no-ops, so the render
// path stays sync. The await is wrapped in IIFE rather than top-level so we
// don't change the file shape (Vite SWC config preserves top-level await
// but the bundler-side service worker doesn't).
void (async () => {
  const { migrateLegacyStorageIfNeeded, hydratePersistedStorage } = await import('./lib/secureStorage');
  await migrateLegacyStorageIfNeeded();
  await hydratePersistedStorage();

  // On native Capacitor (Android/iOS) populate --safe-area-inset-* CSS
  // variables from the OS in native code. Android WebView's env() values
  // Safe-area insets: @capacitor-community/safe-area v8 enables itself
  // automatically and acts purely as a polyfill — on Chromium <140 it writes
  // the real inset values into the standard env(safe-area-inset-*) CSS vars;
  // on newer webviews it leaves the native env() values untouched. There is no
  // longer an imperative enable()/config API (removed in v8), so there is
  // nothing to call here — CSS env(safe-area-inset-*) is the single source of
  // truth on every platform. (v6 exposed SafeArea.enable({ config }); that
  // method no longer exists and calling it threw "enable is not a function".)

  // memoryCache is now warm — trigger Zustand persist's deferred hydration so
  // the auth store reads refreshToken/user from it before App renders. The
  // store has skipHydration: true to suppress its automatic read, which would
  // otherwise fire at module-load time when memoryCache is still empty and
  // wipe the persisted state by writing back nulls. See authStore.ts.
  const { useAuthStore } = await import('./store/authStore');
  await useAuthStore.persist.rehydrate();

  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );

  // Tell the Capgo live-update plugin this JS bundle booted successfully.
  // Must fire on every native launch — skipping it makes the plugin assume
  // the update is broken and roll back to the previous bundle on next
  // launch. No-op on the web (isNativePlatform() false), and wrapped in
  // try/catch like the other native-only calls above in case the plugin
  // isn't registered yet (stale native build predating this addition).
  try {
    const { isNativePlatform } = await import('./lib/runtime');
    if (isNativePlatform()) {
      const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
      await CapacitorUpdater.notifyAppReady();
    }
  } catch (err) {
    console.warn('CapacitorUpdater.notifyAppReady failed', err);
  }
})();
